import React from "react";
import type { CampaignReportResponse } from "../../types";
import { AiInsightsSection } from "../../components/AiInsightsSection";
import { PAGE_BG } from "../../config/theme";
import {
  AjinomotoCampaignDetailsSection,
  AjinomotoDailyTrendSection,
  AjinomotoEngagementSection,
  AjinomotoHeader,
  AjinomotoLiftAnalysisSection,
  AjinomotoOverallPerformanceSection,
  AjinomotoProductComparisonSection,
  AjinomotoProductVisualizationsSection,
  type SectionToggleControls,
} from "./AjinomotoGyozaLayoutSections";
import { buildAjinomotoLayoutViewModel } from "./ajinomotoGyozaLayoutData";

interface AjinomotoGyozaLayoutProps {
  data: CampaignReportResponse;
  reportId?: string;
}

export const AjinomotoGyozaLayout: React.FC<AjinomotoGyozaLayoutProps> = ({
  data,
  reportId,
}) => {
  const { summary, products, lift_metrics, campaigns, daily_metrics } = data;
  const [hiddenSections, setHiddenSections] = React.useState<
    Record<string, boolean>
  >({});

  const hideSection = (sectionId: string) =>
    setHiddenSections((prev) => ({ ...prev, [sectionId]: true }));

  const restoreSection = (sectionId: string) =>
    setHiddenSections((prev) => ({ ...prev, [sectionId]: false }));

  const isSectionHidden = (sectionId: string) =>
    Boolean(hiddenSections[sectionId]);

  const sectionControls: SectionToggleControls = {
    hideSection,
    restoreSection,
    isSectionHidden,
  };

  const viewModel = buildAjinomotoLayoutViewModel(data);

  return (
    <div className={`py-16 px-8 ${PAGE_BG}`} data-dashboard>
      <div className="max-w-7xl mx-auto">
        <AjinomotoHeader data={data} />

        <AiInsightsSection data={data} reportId={reportId} />

        <AjinomotoOverallPerformanceSection
          summary={summary}
          avgDailySales={viewModel.avgDailySales}
          avgDailySpend={viewModel.avgDailySpend}
          averageRoas={viewModel.averageRoas}
          sectionControls={sectionControls}
        />

        {products && products.length > 0 && (
          <>
            <AjinomotoProductComparisonSection
              products={viewModel.productCards}
              sectionControls={sectionControls}
            />
            <AjinomotoEngagementSection
              products={viewModel.productCards}
              totalAddToCart={viewModel.totalAddToCart}
              totalInStoreUnits={viewModel.totalInStoreUnits}
              productColors={viewModel.productColors}
              sectionControls={sectionControls}
            />
            <AjinomotoProductVisualizationsSection
              products={viewModel.productCards}
              productColors={viewModel.productColors}
              totalSales={summary.total_sales}
              sectionControls={sectionControls}
            />
          </>
        )}

        {lift_metrics && viewModel.liftSummary && (
          <AjinomotoLiftAnalysisSection
            liftMetrics={lift_metrics}
            liftSummary={viewModel.liftSummary}
            sectionControls={sectionControls}
          />
        )}

        {daily_metrics && daily_metrics.length > 0 && (
          <AjinomotoDailyTrendSection
            totalDays={summary.total_days}
            trendData={viewModel.dailyTrendData}
            sectionControls={sectionControls}
          />
        )}

        {campaigns && campaigns.length > 0 && (
          <AjinomotoCampaignDetailsSection
            campaignCount={summary.campaign_count}
            campaigns={viewModel.campaignRows}
            sectionControls={sectionControls}
          />
        )}
      </div>
    </div>
  );
};
